<template>
  <ColumnChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    :categories="categories"
    :has-data="false"
    :card-options="cardOptions"
    :show-granularity="false"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import ColumnChart from '@/components/cards/models/ColumnChart';
import sentimentModel from '@/mixins/sentiment-model';
import cardsModel from '@/mixins/cards-model';
import { POST_FIELDS, OPERATION, ENTITIES } from '@/services/charts-service';
import { FILTER_NAMES } from '@/store/modules/cards/constants';
import EventBus from '@/utils/event-bus';
import { mapActions, mapGetters } from 'vuex';
import highchartsBasics from '@/mixins/highcharts-basics';

export default {
  name: 'PostByTagColumnChart',
  components: {
    ColumnChart,
  },
  mixins: [
    sentimentModel,
    cardsModel,
    highchartsBasics,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    label: {
      type: String,
      default: null,
    },
    stackLabels: {
      type: [Boolean, String],
      default: true,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawData: [],
      customFilterData: null,
      pointsParser: 'points',
      categories: [],
      filter: {},
      cardOptions: {
        xAxis: {
          type: 'category',
          categories: [],
        },
        yAxis: {
          stackLabels: {
            enabled: Boolean(this.stackLabels),
            ...(this.stackLabels === 'percentage'
              ? { formatter: (component => {
                return function() {
                  return `${component.sumSeries
                    ? ((this.total / component.sumSeries) * 100).toFixed(1)
                    : 0}%`;
                };
              })(this) }
              : null),
          },
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          series: {
            stacking: 'normal',
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters('dashboard', ['activeDashboardFilters', 'activeGlobalFilter', 'activeDashboard']),
    series() {
      const filteredValues = this.filter[FILTER_NAMES.TAG]?.value;
      const { pointsParser } = this;
      const data = [...this.rawData];

      let rawSeries = [];
      if (this.label) {
        data.forEach(p => {
          p.y = this.label;
          return p;
        });
      }
      if (filteredValues) {
        const filteredSeries = data?.filter(point => {
          return filteredValues.includes(point.x) || filteredValues.includes(point.y);
        }) || [];
        rawSeries = this.normalizeSentimentResponseFromChartService(filteredSeries, pointsParser);
      }
      rawSeries = this.normalizeSentimentResponseFromChartService(data, pointsParser, this.hideNull);
      return rawSeries;
    },
    sumSeries() {
      return this.series.reduce((sum, serie) => {
        return sum + serie.data.reduce((sum, point) => sum + point.y, 0);
      }, 0);
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      const grouping = [POST_FIELDS.TAG];

      this.getChartsService({
        itemId: this.itemId,
        method: 'getAggregatedData',
        points_parser: this.pointsParser,
        entity: ENTITIES.POSTS,
        params: {
          grouping,
          operators: OPERATION.COUNT,
          datePortion: this.getDatePortion(options),
        },
      })
        .then((response) => {
          if (this.$attrs.filter) {
            this.filter = this.$attrs.filter?.tags || this.activeGlobalFilter?.tags;
          }
          this.rawData = this.removeInvalidTags(response);
          this.categories = this.constructCategories(this.rawData);
          this.requestStatus = 'success';
          if (this.$attrs.filter) {
            this.filter = this.$attrs.filter;
          }

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch((err) => {
          console.error(err);
          this.requestStatus = 'error';
        });
    },
    constructCategories(raw) {
      let categories = [];

      if (Array.isArray(raw)) {
        categories = raw.map(item => item.x);
      } else if (raw && typeof raw === 'object') {
        categories = [raw.x];
      }
      return categories;
    },
    normalizeSeries(series) {
      if (series.length === 1) {
        series[0].data.sort((first, second) => first.x - second.x);

        return [{
          ...series[0],
          data: series[0].data?.slice(0, this.maxItemsToBeDisplayed),
        }];
      }
      return series.map(serie => {
        const newData = serie.data.filter(each => {
          return each.x < this.maxItemsToBeDisplayed;
        });

        return {
          ...serie,
          data: newData,
        };
      });
    },
    removeInvalidTags(response) {
      const tags = Array.isArray(this.filter?.value) ? this.filter.value : [];
      if (!tags.length) {
        return response?.points || [];
      }
      return response?.points?.filter(({ x }) => this.filter.value.includes(x)) || [];
    },
  },
};
</script>
